import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import { useMeeting } from '../../../../context/MeetingContext';
import { useSession } from '../../../../context/SessionContext';
import { upsertMeeting } from '../../../../apiconfig/api';

const PersistMeeting: React.FC = () => {
  const navigate = useNavigate();
  const meeting = useMeeting();
  const { sessionEmployerId, isLoggedIn } = useSession();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const persistMeetingData = async () => {
      if (!isLoggedIn || !sessionEmployerId) {
        setError('You must be logged in to persist a meeting');
        setLoading(false);
        return;
      }

      if (!meeting.applicantIds || meeting.applicantIds.length === 0) {
        setError('No applicants selected for this meeting');
        setLoading(false);
        return;
      }

      if (!meeting.location || !meeting.scheduledAt) {
        setError('Meeting details are incomplete. Please go back and complete the meeting setup.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Prepare meeting payload from context
        const meetingPayload = {
          scheduledAt: meeting.scheduledAt.toISOString(),
          duration: meeting.duration,
          location: meeting.location,
          notes: meeting.notes || '',
          timezone: meeting.timezone || 'UTC',
          applicantIds: meeting.applicantIds,
        };

        // Persist the meeting
        await upsertMeeting(sessionEmployerId, meeting.id, meetingPayload);

        // Redirect to meeting success page
        navigate(`/hiring/meetings/${meeting.id}/success`);
      } catch (err: any) {
        console.error('Failed to persist meeting:', err);
        setError(err.message || 'Failed to persist meeting. Please try again.');
        setLoading(false);
      }
    };

    persistMeetingData();
  }, [meeting, sessionEmployerId, isLoggedIn, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f4f4',
          gap: 2,
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body-lg" color="neutral">
          Saving your meeting...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f4f4',
          padding: '20px',
        }}
      >
        <Alert color="danger" variant="soft" sx={{ maxWidth: '400px' }}>
          <Typography level="body-lg" sx={{ mb: 1 }}>
            Failed to save meeting
          </Typography>
          <Typography level="body-sm">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return null;
};

export default PersistMeeting;
